import { createApi, fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { definirTokens, limparTokens } from '../store/auth-slice';
import { limparAdminToken } from '../store/admin-auth-slice';
import { obterTenantArmazenado } from '../lib/tenant';
import { guardarSessaoTotem, obterSessaoTotem } from '../lib/sessao-totem';
import { qsPaginacao, type Paginado, type ListarPaginadoArgs } from '../lib/paginacao';
import type { AcessoModos } from '../lib/modos-cardapio';

const ENDPOINTS_SESSAO_TOTEM = new Set([
  'getContaTotemCheckout',
  'adicionarItensComanda',
  'alterarTaxaServicoTotem',
  'registrarPagamentoTotem',
  'emitirNotaTotem',
  'imprimirResumoTotem',
  'conteudoImpressaoTotem',
]);

const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: (headers, { endpoint, getState }) => {
    const state = getState() as RootState;
    const ehAdmin = endpoint?.startsWith('admin');
    const token = ehAdmin ? state.adminAuth.token : state.auth.accessToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (!ehAdmin) {
      const tenant = obterTenantArmazenado();
      if (tenant) headers.set('x-tenant', tenant);
      if (endpoint && ENDPOINTS_SESSAO_TOTEM.has(endpoint)) {
        const sessao = obterSessaoTotem();
        if (sessao) headers.set('x-totem-sessao', sessao);
      }
    }
    return headers;
  },
});

const baseQueryComRefresh: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extra) => {
  let resultado = await baseQuery(args, api, extra);
  if (resultado.error?.status === 401) {
    const ehAdmin = api.endpoint?.startsWith('admin');
    if (ehAdmin) {
      // Admin: o token vem do Sischef e não tem refresh. Limpa pra forçar
      // re-login (usuário precisa entrar de novo pelo Sischef).
      api.dispatch(limparAdminToken());
    } else {
      // Cliente delivery: tenta renovar com refresh token.
      const state = api.getState() as RootState;
      const refreshToken = state.auth.refreshToken;
      if (refreshToken) {
        const refresh = await baseQuery({ url: '/auth/refresh', method: 'POST', body: { refreshToken } }, api, extra);
        const dados = refresh.data as { accessToken: string; refreshToken: string } | undefined;
        if (dados) {
          api.dispatch(definirTokens({ accessToken: dados.accessToken, refreshToken: dados.refreshToken }));
          resultado = await baseQuery(args, api, extra);
        } else {
          api.dispatch(limparTokens());
        }
      } else {
        api.dispatch(limparTokens());
      }
    }
  }

  const dados = resultado.data as { sessaoTotem?: unknown } | undefined;
  if (typeof dados?.sessaoTotem === 'string') guardarSessaoTotem(dados.sessaoTotem);

  return resultado;
};

export interface Usuario {
  id: number;
  nome: string;
  email: string;
  cpf: string | null;
  telefone: string | null;
}

/** Sessão autenticada do cliente delivery (login concluído). */
export interface SessaoCliente {
  accessToken: string;
  refreshToken: string;
  primeiroAcesso: boolean;
  usuario: Usuario;
}

/** Código validado, mas o e-mail ainda não tem cadastro: falta nome/CPF. */
export interface PrecisaCadastro {
  precisaCadastro: true;
  cadastroToken: string;
}

export type ValidarCodigoResposta = SessaoCliente | PrecisaCadastro;

/** Cashback do cliente logado nesta loja (`GET /me/cashback`). Só existe no delivery. */
export interface CashbackDoCliente {
  /** false = nada de cashback aparece: uso desligado na loja, programa pausado ou cashback fora do ar. */
  disponivel: boolean
  /** O CPF ou o telefone do perfil achou uma conta no cashback. false com `disponivel` = convite de cadastro. */
  encontrado: boolean
  /** Saldo que dá para usar agora, em reais. */
  saldoDisponivel: number
  /** Quanto dos itens a loja aceita pagar com cashback, em %. Zero quer dizer sem teto. */
  percentualMaxUso: number
}

/** Programa de cashback da loja, que o admin só mostra: o limite se altera no painel do Sischef Cashback. */
export interface ProgramaCashbackLoja {
  /** false = o cashback não respondeu: o admin não mostra nada. */
  disponivel: boolean
  programaAtivo: boolean
  /** Quanto dos itens o cashback paga em cada pedido, em %. Zero quer dizer sem limite. */
  percentualMaxUso: number
}

/** O que o cadastro fez: criou a conta, assumiu a que o balcão tinha criado, ou a conta já existia com senha. */
export const resultadosCadastroCashback = ['CONTA_CRIADA', 'CONTA_ASSUMIDA', 'CONTA_JA_EXISTIA'] as const

export type ResultadoCadastroCashback = (typeof resultadosCadastroCashback)[number]

/** Resposta do cadastro no cashback: a situação depois dele, mais o que aconteceu com a conta. */
export interface CadastroNoCashback extends CashbackDoCliente {
  resultado: ResultadoCadastroCashback
}

/** Corpo do cadastro no cashback (`POST /me/cashback/cadastro`). CPF e telefone vão com ou sem máscara. */
export interface DadosCadastroCashback {
  nome: string
  cpf: string
  telefone: string
  /** Senha nova, do site do cashback. Só passa pelo cardápio: não é gravada nem devolvida. */
  senha: string
  aceiteTermos: true
}

export type TipoEndereco = 'CASA' | 'TRABALHO' | 'OUTROS';

export interface Endereco {
  id: number;
  apelido: string | null;
  tipo: TipoEndereco | null;
  cep: string | null;
  logradouro: string;
  numero: string | null;
  complemento: string | null;
  bairro: string;
  cidade: string;
  uf: string;
  referencia: string | null;
  latitude: string | null;
  longitude: string | null;
  principal: boolean;
}

export interface NovoEndereco {
  apelido?: string;
  tipo?: TipoEndereco;
  cep?: string;
  logradouro: string;
  numero?: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
  referencia?: string;
  latitude?: number;
  longitude?: number;
  principal?: boolean;
}

/**
 * Acompanhamento da NFC-e do pedido do totem, derivado no Sischef.
 * `PROCESSANDO` é o estado normal enquanto a nota vai à SEFAZ; `ERRO` traz o
 * motivo em `mensagem` (inclusive quando o XML nem chegou a ser transmitido).
 */
/** Usuário do Sischef elegível como emissor da NFC-e do totem. */
export interface UsuarioEmissorNfce {
  username: string;
  nome: string | null;
}

export interface StatusNotaFiscalTotem {
  situacao: 'PROCESSANDO' | 'AUTORIZADA' | 'ERRO';
  numero: number | null;
  mensagem: string | null;
}

export interface SenhaTotem {
  sequencial: number | null;
  notaFiscal: StatusNotaFiscalTotem | null;
}

/** NFC-e do totem de pedidos: não emite, emite e imprime, ou emite e pergunta se imprime. */
export type TotemNota = 'NAO' | 'SEMPRE' | 'PERGUNTAR';
/** NFC-e do totem de checkout: imprime sempre, ou pergunta ao cliente se quer o papel. */
export type TotemCheckoutNota = 'SEMPRE' | 'PERGUNTAR';
export const consumosTotem = ['LOCAL', 'LEVAR'] as const;
export type ConsumoTotem = (typeof consumosTotem)[number];
export const modosConsumoTotem = ['LOCAL', 'LEVAR', 'AMBOS'] as const;
export type ModoConsumoTotem = (typeof modosConsumoTotem)[number];

export type ArgCatalogo = CanalCardapio | { canal: CanalCardapio; consumo?: ConsumoTotem };

export const ROTULO_CONSUMO: Record<ConsumoTotem, string> = {
  LEVAR: 'Para levar',
  LOCAL: 'Consumir no local',
};

/** Resposta do Sischef ao imprimir a NFC-e já emitida (`impressa: false` traz o motivo). */
export interface ResultadoImpressaoNotaTotem {
  impressa: boolean;
  mensagem: string | null;
}

/** Para onde o banner do topo do cardápio leva quando o cliente toca nele. */
export type DestinoBanner =
  | { tipo: 'NENHUM' }
  | { tipo: 'PRODUTO'; idProduto: number }
  | { tipo: 'GRUPO'; idGrupo: number }
  | { tipo: 'PROMOCOES' }

export type TipoDestinoBanner = DestinoBanner['tipo']

export interface BannerCardapio {
  id: string
  urlImagem: string
  /** Texto alternativo da imagem e nome no analytics; não aparece escrito no banner. */
  nome?: string | null
  destino: DestinoBanner
  /** 'YYYY-MM-DD', inclusivo, no fuso da loja. */
  inicio?: string | null
  fim?: string | null
}

export interface ConfiguracaoLoja {
  id: number;
  idUnidade: number;
  nome: string;
  /** `false` = loja excluída (exclusão lógica); só o suporte recebe lojas nesse estado. */
  ativo: boolean;
  dataExclusao: string | null;
  usuarioExclusao: string | null;
  formaRetirada: string;
  /** Faixa de preparo/entrega exibida ao cliente (minutos). */
  tempoMinPreparo: number | null;
  tempoMaxPreparo: number | null;
  /** Tempo médio de retirada no balcão (minutos). */
  tempoRetirada: number | null;
  /** Tempo estimado de entrega/delivery (minutos). */
  tempoEntrega: number | null;
  /**
   * Pedido mínimo por canal (`numeric` do Postgres → chega como string).
   * `valorMinimoPedido` é o mínimo do DELIVERY.
   */
  valorMinimoPedido: string | null;
  /** Taxa de conveniência (delivery): valor fixo somado ao total, exibido só na confirmação. */
  taxaConveniencia: string | null;
  freteGratisAPartirDe?: number | null;
  /** Agendamento no delivery: 'SEMPRE' (padrão), 'DESATIVADO' ou 'SO_FECHADA' (só com a loja fechada). */
  agendamentoDelivery: 'SEMPRE' | 'DESATIVADO' | 'SO_FECHADA';
  /** Como a taxa de entrega é cobrada: por bairro ou por distância. */
  estrategiaTaxa: 'BAIRRO' | 'KM';
  valorMinimoTotem: string | null;
  valorMinimoMesaComanda: string | null;
  urlLogo: string | null;
  urlCover: string | null;
  /** Até 5 imagens de fundo do modo TOTEM (proteção de tela). */
  imagensTotem: string[] | null;
  /** Até 5 imagens de fundo da proteção de tela do TABLET (na horizontal). */
  imagensTablet: string[] | null;
  /** Banners do topo do cardápio, em ordem: na loja só os do período, no admin todos. Ausente em backend antigo. */
  banners?: BannerCardapio[]
  tipoIdentificadorPrincipal: 'MESA' | 'COMANDA' | null;
  tipoIdentificadorSecundario: 'MESA' | 'COMANDA' | 'NENHUM' | null;
  identificadorSecundarioObrigatorio: boolean;
  /** Modo do tablet: 'MESA' (só nº da mesa) ou 'COMANDA' (pergunta a comanda). Null herda de tipoIdentificadorPrincipal. */
  tabletIdentificador: 'MESA' | 'COMANDA' | null;
  /** Como o cliente informa a comanda no tablet. Só vale com tabletIdentificador = COMANDA. */
  tabletEntradaComanda?: 'DIGITAR' | 'QRCODE' | 'AMBOS';
  /** Identificador principal do totem checkout: 'MESA' ou 'COMANDA'. */
  totemCheckoutIdentificador: 'MESA' | 'COMANDA' | null;
  /**
   * O que o TOTEM CHECKOUT imprime ao fim do pagamento — mesmo eixo do totem de
   * pedidos, sem a senha (aqui a conta já é de uma mesa/comanda identificada).
   */
  totemCheckoutImpressao: 'NENHUMA' | 'RESUMO' | 'NFCE';
  /** Com NFCE: a nota sai sempre no papel, ou o cliente escolhe. Ausente em backend anterior: vale PERGUNTAR. */
  totemCheckoutNota?: TotemCheckoutNota;
  /** Os dois totens: pergunta o CPF para a NFC-e. Ausente em backend anterior: pergunta. */
  totemPerguntarCpfNota?: boolean;
  /** Os dois totens: imprime a via do cliente da maquininha. Ausente em backend anterior: imprime. */
  totemImprimirComprovanteTef?: boolean;
  imagensTotemCheckout: string[] | null;
  taxaServicoDispensavel: boolean;
  totemBalancaAtiva: boolean;
  /** Totem de checkout: o resumo aceita produto passado no leitor e mostra a dica. Ausente em backend anterior: desligado. */
  totemCheckoutLeitorCodigoBarras?: boolean;
  totemPagerAtivo: boolean;
  totemPagerMinimo: number | null;
  totemPagerMaximo: number | null;
  totemConsumo: ModoConsumoTotem;
  /** DELIVERY: aceita pedido sem login, só com pagamento na entrega. */
  permitirPedidoSemLogin?: boolean;
  /** Pedidos do cardápio dão cashback. Ausente = ligado. NÃO diz se o programa de cashback está ativo. */
  cashbackAtivo?: boolean
  /** DELIVERY: o cliente logado usa o cashback como desconto no pedido. Ausente (backend antigo) = desligado. */
  cashbackUsoAtivo?: boolean
  /** Há um usuário emissor da NFC-e configurado? O login em si nunca sai do backend. */
  totemEmissorNfceConfigurado: boolean;
  /** Existe local de impressão na LOJA (fallback do aparelho). Sem nenhum dos dois, a senha não sai. */
  totemLocalImpressaoConfigurado: boolean;
  tabletLocalImpressaoConfigurado: boolean;
  /**
   * LEGADO do totem de pedidos, derivado das três opções abaixo (NFCE se há nota, senão
   * RESUMO se há resumo). Continua na resposta para o bundle antigo dos totens em kiosk;
   * código novo lê as opções via `opcoesImpressaoTotem`.
   */
  totemImpressao: 'NENHUMA' | 'RESUMO' | 'NFCE';
  /** Totem de pedidos: imprime a senha ao finalizar. Ausente em backend anterior às opções. */
  totemImprimirSenha?: boolean;
  /** Totem de pedidos: o Sischef imprime o resumo do pedido. */
  totemImprimirResumo?: boolean;
  /**
   * Totem de pedidos, NFC-e. `PERGUNTAR` emite do mesmo jeito e muda a tela de confirmação:
   * autorizada a nota, o cliente escolhe se ela sai no papel.
   */
  totemNota?: TotemNota;
  /** Rótulos das opções do botão "chamar garçom" do tablet. */
  tabletChamarGarcomOpcoes: string[] | null;
  /** IDs de rastreio (opcionais) — alimentam o Pixel/GA do app. */
  pixelFacebookId: string | null;
  googleAnalyticsId: string | null;
  /** Contatos da loja (opcionais) — usados quando o pedido não é confirmado. */
  whatsapp: string | null;
  instagram: string | null;
}

export interface IdentificadorLoja {
  id: number;
  idConfiguracaoCardapio: number;
  tipo: 'MESA' | 'COMANDA' | 'TOTEM' | 'TOTEM_CHECKOUT' | 'TABLET' | 'DELIVERY' | 'VITRINE';
  numero: string | null;
  /** UUID (mesa/comanda/totem) ou slug/domínio (delivery/vitrine). */
  codigo: string;
  /** DELIVERY/VITRINE: se true, `codigo` é um domínio completo (cardapiox.com.br). */
  dominioCompleto: boolean;
  /** Ponto de acesso ativo. Inativar (lixeiro) faz soft-delete (ativo=false). */
  ativo: boolean;
}

export type TipoIdentificador = IdentificadorLoja['tipo'];

/** O PIN saiu daqui: é único da loja (`pinTotem` da configuração, aba Gerais). */
export interface ConfigPontoAcessoOverride {
  totemCheckoutIdentificador?: 'MESA' | 'COMANDA' | null;
  totemCheckoutImpressao?: ConfiguracaoLoja['totemCheckoutImpressao'];
  totemCheckoutNota?: TotemCheckoutNota;
  totemPerguntarCpfNota?: boolean;
  totemImprimirComprovanteTef?: boolean;
  totemLocalImpressao?: string | null;
  totemUsuarioNfce?: string | null;
  taxaServicoPercentual?: string | null;
  taxaServicoDispensavel?: boolean;
  totemBalancaAtiva?: boolean;
  totemCheckoutLeitorCodigoBarras?: boolean;
  totemConsumo?: ModoConsumoTotem;
  imagensTotemCheckout?: string[] | null;
  imagensTablet?: string[] | null;
  /** Totem de PEDIDOS: tudo o que a aba dele configura é por aparelho. */
  imagensTotem?: string[] | null;
  totemPagerAtivo?: boolean;
  totemPagerMinimo?: number | null;
  totemPagerMaximo?: number | null;
  totemImprimirSenha?: boolean;
  totemImprimirResumo?: boolean;
  totemNota?: TotemNota;
  /** O backoffice manda número; o backend devolve no formato da coluna (`"30.00"`). */
  valorMinimoTotem?: number | string | null;
}

export type ChaveConfigPontoAcesso = keyof ConfigPontoAcessoOverride;

export interface PontoAcessoConfiguravel {
  id: number;
  idConfiguracaoCardapio: number;
  tipo: TipoIdentificador;
  numero: string | null;
  codigo: string;
  ativo: boolean;
  configuracao: ConfigPontoAcessoOverride | null;
}

/** Resumo por tipo (card "Pontos de acesso"): contagem + amostra de itens. */
export interface ResumoIdentificadorTipo {
  total: number;
  ativos: number;
  itens: IdentificadorLoja[];
}

export type ResumoIdentificadores = Record<TipoIdentificador, ResumoIdentificadorTipo>;

/** Endereço da empresa (somente leitura) — usado no card "Retirar no estabelecimento". */
export interface EnderecoEmpresaLoja {
  logradouro: string;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
}

export interface LojaAtualResponse {
  configuracao: ConfiguracaoLoja;
  identificador: IdentificadorLoja;
  enderecoEmpresa: EnderecoEmpresaLoja | null;
  cnpjEmpresa: string | null;
  impressaoLocal: { servidor: string } | null;
}

/** Local de impressão do print server (setup do tablet). Campos best-effort. */
export interface LocalImpressaoTablet {
  id?: number | string;
  _id?: string;
  nome?: string;
  descricao?: string;
  /** Sigla/chave do local — é o que vai pro print server (campo localImpressao), não a descrição. */
  chave?: string;
}

export interface ItemConta {
  nome: string | null;
  codigoExterno: string | null;
  quantidade: number | null;
  valorUnitario: number | null;
  valorTotal: number | null;
  observacao: string | null;
  subItens: ItemConta[];
}

export interface ItemAdicionarComanda {
  idProduto: number;
  nome: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
  observacao?: string | null;
  passos?: Array<{
    idProdutoPasso: number;
    descricao?: string | null;
    valorPasso?: number | null;
    itens: Array<{
      idTabelaPrecoItem: number;
      idProduto: number;
      nome?: string | null;
      quantidade: number;
      valor: number;
    }>;
  }>;
}

export type MotivoBloqueioTotem = 'PEDIDO_PAGO' | 'PEDIDO_JA_IMPORTADO' | 'PEDIDO_NAO_IMPORTAVEL';

export const MENSAGEM_BLOQUEIO_TOTEM: Record<MotivoBloqueioTotem, string> = {
  PEDIDO_PAGO: 'Esta conta já foi fechada. Fale com o atendente.',
  PEDIDO_JA_IMPORTADO: 'Esta conta já está no caixa. O pagamento e novos itens são feitos com o atendente.',
  PEDIDO_NAO_IMPORTAVEL: 'Esta conta ainda está sendo processada. Aguarde um instante ou chame o atendente.',
};

export interface ContaTablet {
  tipo: 'MESA' | 'COMANDA';
  /** MESA|COMANDA do modelo da loja no Sischef — define o eixo da conta. */
  modeloIdentificador?: 'MESA' | 'COMANDA';
  identificador: string;
  quantidadePedidos: number;
  total: number;
  emiteNfce?: boolean;
  sessaoTotem?: string;
  pedidos: Array<{
    idPedidoIntegracao: number | null;
    codigoIntegracao: string | null;
    descricao: string | null;
    dataPedido: string | null;
    total: number;
    sequencial: number | null;
    identificadorPrincipal?: string | null;
    identificadorSecundario: string | null;
    /** Origem do lançamento no Sischef: CS = cardápio; SO/PO/PF/SC = balcão. */
    tipoImportacao?: string | null;
    nomeCliente?: string | null;
    subtotal?: number;
    taxaServico?: number;
    desconto?: number;
    taxaServicoDispensada?: boolean;
    taxaServicoDispensadaValor?: number;
    valorRecebido?: number;
    bloqueadoNoTotem?: boolean;
    motivoBloqueio?: MotivoBloqueioTotem | null;
    itens?: ItemConta[];
  }>;
}

export interface ResultadoTaxaServicoTotem {
  ok: boolean;
  idPedidoIntegracao: number;
  dispensada: boolean;
  alterado: boolean;
  taxaServico: number;
  taxaServicoAnterior: number;
  taxaServicoDispensadaValor: number;
  subtotal: number;
  desconto: number;
  total: number;
  valorRecebido: number;
  itens?: ItemConta[];
}

/** Município atendido pelo delivery (UF já resolvida). */
export interface MunicipioLoja {
  id: number;
  nome: string;
  uf: string;
}

/** Bairro com taxa de entrega cadastrada — usado no combo do cadastro de endereço. */
export interface BairroLoja {
  id: number;
  nome: string;
  municipio: string;
  uf: string;
  /** Valor da taxa — string por causa do `numeric` do drizzle. */
  valor: string;
}

export interface HorariosLojaResponse {
  /** Aberta AGORA — mesma regra que bloqueia pedidos fora de horário. */
  aberta: boolean;
  horarios: HorarioFuncionamentoLoja[];
}

export interface AvaliacaoPublica {
  id: number;
  estrelas: number;
  comentario: string | null;
  dataAvaliacao: string;
  /** Nome mascarado pra privacidade (ex.: "João S.") ou null se anônimo. */
  clienteNome: string | null;
}

export interface ResumoAvaliacoesLoja {
  total: number;
  media: number;
  distribuicao: { 1: number; 2: number; 3: number; 4: number; 5: number };
  avaliacoes: AvaliacaoPublica[];
}

// ===== Tipos admin (CRUDs do backoffice) =====

export type TipoPagamentoLoja = 'LOCAL' | 'ONLINE' | 'TEF';
export type FormaPagamentoOpcao = 'DINHEIRO' | 'CARTAO' | 'PIX' | 'OUTROS';

export interface FormaPagamentoLoja {
  id: number;
  idConfiguracaoCardapio: number;
  /** LOCAL = paga na entrega/retirada; ONLINE = gateway (delivery); TEF = pinpad ELGIN (totem). */
  tipo: TipoPagamentoLoja;
  /** DINHEIRO | CARTAO | PIX | OUTROS */
  forma: FormaPagamentoOpcao;
  /** ID da forma de pagamento associada no Sischef. */
  codigo: string;
  descricao: string;
  /** Config de integração (configuracao_integracao do Sischef) — só ONLINE/TEF. */
  idConfiguracaoIntegracao?: number | null;
  /** CSV de métodos habilitados (ex.: "PIX,CREDIT_CARD,DEBIT_CARD") — só ONLINE/TEF. */
  metodos?: string | null;
  /** Canais do cardápio (flags entre colchetes, ex.: "[CARDAPIO_DELIVERY]"). NULL/vazio = todos. */
  visibilidadeFlags?: string | null;
  /** Pix estático (só LOCAL + PIX): com chave, o pedido confirmado mostra QR Code e "copia e cola". */
  pixChave?: string | null;
  pixNomeRecebedor?: string | null;
  pixCidade?: string | null;
  ordem: number;
  ativo: boolean;
  dataCriacao: string;
}

/** Item do combo "Associar à forma de pagamento no Sischef". */
export interface FormaPagamentoSischef {
  id: number;
  descricao: string;
}

/** Config de integração de pagamento (ASAAS/ELGIN) para o combo do editor — sem token. */
export interface IntegracaoPagamentoOpcao {
  id: number;
  descricao: string;
}

/** Dados para o lojista cadastrar o webhook de pagamento na conta ASAAS dele. */
export interface WebhookAsaasInfo {
  url: string;
  /** Token exclusivo da loja — vai no campo "Token de autenticação" do ASAAS. */
  token: string;
  /** Eventos que precisam ficar marcados no webhook. */
  eventos: string[];
}

/**
 * Resultado da conferência da chave Pix na conta do provedor.
 * NAO_VERIFICADO = a consulta falhou; não afirma que falta chave.
 */
export type SituacaoChavePix = 'ATIVA' | 'PENDENTE' | 'AUSENTE' | 'NAO_VERIFICADO';

export interface DiagnosticoPixConta {
  situacao: SituacaoChavePix;
  quantidadeAtivas: number;
}

/** Endereço da empresa — vem da Pessoa associada à Unidade no Sischef (somente leitura). */
export interface EnderecoEmpresaSischef {
  nomeUnidade: string;
  nomeFantasia: string | null;
  nomePessoa: string | null;
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cep: string | null;
  municipio: string | null;
  uf: string | null;
}

export interface TaxaEntregaBairroLoja {
  id: number;
  idConfiguracaoCardapio: number;
  nomeBairro: string;
  municipio: string;
  uf: string;
  /** Valor monetário — backend devolve como string por causa do `numeric` no drizzle. */
  valor: string;
  ativo: boolean;
  dataCriacao: string;
}

/** Taxa de entrega (produto ENTREGA) do Sischef candidata à importação. */
export interface TaxaImportavelSischef {
  idProduto: number;
  descricao: string;
  /** Valor de venda do produto no Sischef (doublePrecision → número). */
  valor: number;
  /** Já vinculada a uma taxa do cardápio (por id_produto). */
  jaImportado: boolean;
}

export interface ImportarTaxasSischefResposta {
  /** Município/UF derivado do endereço da empresa, aplicado às taxas importadas. */
  municipioDestino: { municipio: string; uf: string };
  itens: TaxaImportavelSischef[];
}

/** Estado da integração Google Maps da loja, que a cobrança por KM exige. */
export interface DiagnosticoTaxaKm {
  /** A empresa tem integração Google Maps ativa no Sischef. */
  temChave: boolean;
  /** A chave funciona em chamada de servidor e o endereço da loja foi localizado. */
  ok: boolean;
  mensagem: string;
  /** Endereço da loja como o Google entendeu — o gestor confere se é o dele. */
  enderecoNormalizado?: string;
  precisao?: string;
  /**
   * De onde saiu a origem: `CADASTRO`/`CONFIG` é coordenada cadastrada,
   * `GEOCODE` é o Google interpretando o texto do endereço.
   */
  fonteOrigem?: 'CONFIG' | 'CADASTRO' | 'GEOCODE';
}

export interface TaxaEntregaKmLoja {
  id: number;
  idConfiguracaoCardapio: number;
  /** Teto da faixa em KM. String por causa do `numeric` do drizzle. */
  kmFinal: string;
  /** Valor cobrado na faixa. String por causa do `numeric` do drizzle. */
  valor: string;
  ativo: boolean;
  dataCriacao: string;
}

export interface AvaliacaoAdminItem {
  avaliacao: {
    id: number;
    idPedidoCardapio: string;
    estrelas: number;
    comentario: string | null;
    removidoPorAdmin: boolean;
    dataAvaliacao: string;
  };
  pedido: {
    id: string;
    shortId: string | null;
    situacao: string;
    clienteNome: string | null;
    dataCriacao: string;
  };
}

/** Listagem paginada de avaliações + estatísticas agregadas (sobre todas). */
export interface AvaliacoesAdminPagina extends Paginado<AvaliacaoAdminItem> {
  estatisticas: {
    media: number;
    distribuicao: Record<1 | 2 | 3 | 4 | 5, number>;
  };
}

/** Vínculo município⇆configuração (já vem com nome/uf resolvidos via join). */
export interface MunicipioAtendidoLoja {
  id: number;
  idConfiguracaoCardapio: number;
  idMunicipio: number;
  ativo: boolean;
  nome: string;
  uf: string;
}

/** Item do catálogo master de municípios do Sischef (autocomplete). */
export interface MunicipioMaster {
  id: number;
  nome: string;
  codigoIbge: number;
  uf: string;
  nomeEstado: string;
}

/** Canais (tipos de pedido) do cardápio — espelho de CanalCardapioEnum. */
export type CanalCardapio =
  | 'CARDAPIO_COMANDA_MESA'
  | 'CARDAPIO_DELIVERY'
  | 'CARDAPIO_VITRINE'
  | 'CARDAPIO_TOTEM'
  | 'CARDAPIO_TOTEM_CHECKOUT';

/** Mapa de canais PAUSADOS manualmente (canal ausente = ativo). */
export type PausasCanal = Record<string, { desde: string; por?: string | null }>;

export interface HorarioFuncionamentoLoja {
  id: number;
  idConfiguracaoCardapio: number;
  /** Canal (tipo de pedido) da faixa; `null` = faixa base (global). */
  canal: CanalCardapio | null;
  /** 0 = domingo, 6 = sábado. */
  diaSemana: number;
  /** Formato `"HH:MM:SS"` (postgres `time`). */
  horaInicio: string;
  horaFim: string;
  ativo: boolean;
  dataCriacao: string;
}

export type Canal =
  | 'CARDAPIO_COMANDA_MESA'
  | 'CARDAPIO_DELIVERY'
  | 'CARDAPIO_VITRINE'
  | 'CARDAPIO_TOTEM'
  | 'CARDAPIO_TOTEM_CHECKOUT';
/** `PROPORCIONAL_AO_SABOR` só vem no sub-passo: o adicional custa a fração do sabor a que pertence. */
export type TipoCalculoPasso = 'MAIOR_VALOR' | 'FRACAO' | 'UNITARIO' | 'PROPORCIONAL_AO_SABOR'

export interface ImagemProduto {
  id: number;
  /** URL direta gravada no anexo (legado/fallback). Pode ser null. */
  url: string | null;
  /** Key do S3 — usar com `urlImagemProduto` pra montar a URL via ImageBoss. */
  publicId: string | null;
  preferencial: boolean;
  contentType: string | null;
}

export interface ItemTabelaPreco {
  id: number;
  idProduto: number;
  /** Ordem da opção dentro do passo (tela de ordenação). Nula até ser reordenada. */
  ordem: number | null;
  nomeProduto: string;
  descricao?: string | null;
  /** Foto do produto da opção (a preferencial). `null` = sem foto cadastrada. */
  imagem?: ImagemProduto | null;
  valor: number;
  valorOriginal?: number;
  precoPorHorarioAtivo?: boolean;
  adicionarAutomaticamente: boolean;
  quantidadeAutomatica: number | null;
}

export interface FaixaPrecoProduto {
  quantidadeMinima: number;
  quantidadeMaxima: number | null;
  valorUnitario: number;
}

export interface TabelaPrecoCatalogo {
  id: number;
  descricao: string;
  tipo: string | null;
  itens: ItemTabelaPreco[];
}

export interface PassoProduto {
  id: number;
  ordem: number;
  nivel: number;
  quantidadeMinima: number;
  quantidadeMaxima: number;
  tipoCalculo: TipoCalculoPasso;
  descricaoCardapio: string | null;
  tabelaPreco: TabelaPrecoCatalogo | null;
  /**
   * O passo tinha opções e a loja pausou todas: vem com `itens: []` para a tela avisar, em vez
   * de o passo sumir e o produto abrir sem a escolha obrigatória. Opcional porque catálogo
   * montado antes da v6 não tem o campo (ver VERSAO_CATALOGO).
   */
  indisponivel?: boolean;
  /** Vale para CADA opção escolhida neste passo (adicional por sabor). Ausente no catálogo v6. */
  subPasso?: SubPassoProduto | null
}

/** Sub-passo "Adicionais dos itens do passo": mesmo formato do passo, sem outro sub-passo dentro. */
export type SubPassoProduto = Omit<PassoProduto, 'subPasso'>

export interface ProdutoCatalogo {
  id: number;
  descricao: string;
  descricaoSucinta: string | null;
  descricaoCompleta: string | null;
  descricaoComposicao: string | null;
  informacoesTecnicas: string | null;
  observacoes: string | null;
  advertencias: string | null;
  emPromocao: boolean;
  novo?: boolean;
  destaqueCardapio: boolean;
  valor: number;
  valorOriginal?: number;
  precoPorHorarioAtivo?: boolean;
  precoPorHorarioDescricao?: string | null;
  faixasPreco?: FaixaPrecoProduto[];
  pesavel: boolean;
  produtoLivre: {
    id: number;
    valor: number;
    descricao: string;
    valorOriginal?: number;
    precoPorHorarioAtivo?: boolean;
  } | null;
  abrirPassos: string | null;
  /** Código que o leitor lê; o resumo do totem de checkout lança o produto por ele. Opcional: cache v7 não traz. */
  codigoBarras?: string | null;
  imagens: ImagemProduto[];
  passos: PassoProduto[];
}

export interface GrupoCatalogo {
  id: number;
  descricao: string;
  tipo: string | null;
  cor: string | null;
  produtos: ProdutoCatalogo[];
}

export interface CatalogoResponse {
  grupos: GrupoCatalogo[];
}

/** Endereço obtido do GPS do cliente (reverse geocoding com a chave da loja). */
export interface EnderecoDaCoordenada {
  latitude: number;
  longitude: number;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  municipio?: string | null;
  uf?: string | null;
  cep?: string | null;
  enderecoNormalizado: string;
}

/**
 * Por que a taxa não saiu. Decide a mensagem do checkout: as três primeiras
 * pediam ações opostas e mostravam o mesmo texto, que mandava conferir um
 * endereço em que muitas vezes não havia nada a conferir.
 */
export type MotivoSemTaxa =
  /** Loja sem integração Google ativa ou sem origem. O cliente não resolve. */
  | 'LOJA_SEM_ENTREGA'
  /** O mapa não localizou o endereço do cliente. */
  | 'ENDERECO_NAO_LOCALIZADO'
  /** Há as duas pontas, mas não há caminho de carro entre elas. */
  | 'SEM_ROTA'
  /** Estratégia BAIRRO: o bairro não tem taxa cadastrada. */
  | 'BAIRRO_SEM_TAXA'
  | 'SEM_ENDERECO';

export interface TaxaEntregaResponse {
  estrategia: 'BAIRRO' | 'KM';
  valor: number;
  /** `false` quando não dá para determinar a taxa: bairro sem cadastro, ou
   * endereço que não foi possível localizar no mapa. */
  encontrado: boolean;
  /** Só na estratégia KM: distância usada no cálculo. */
  distanciaKm?: number;
  /**
   * `ESTIMADA` = a taxa saiu de um ponto aproximado. O cliente NÃO vê isso: não
   * há ação que ele possa tomar, e avisar só assustaria. O marcador vai no
   * pedido, para a loja conferir na entrega.
   */
  confianca?: 'EXATA' | 'ESTIMADA';
  /** Preenchido quando `encontrado` é `false`. */
  motivo?: MotivoSemTaxa;
}

export interface CupomValidado {
  valido: boolean;
  codigo: string;
  tipoDesconto: 'PERCENTUAL' | 'VALOR_FIXO' | 'FRETE_GRATIS' | 'FRETE_PERCENTUAL' | 'FRETE_VALOR'
  valorDesconto: number;
  valorDoCupom?: number
  /** `REDE` = cupom válido em todas as lojas da rede desta loja. */
  escopo?: 'LOJA' | 'REDE';
}

/** Rede de lojas (franquia) — ver `docs/specs/cupom-rede.md`. */
export interface RedeCardapio {
  id: number;
  nome: string;
  idFranquia: number | null;
  idUnidadeDona: number;
  ativo: boolean;
}

export interface LojaCandidataRede {
  idConfiguracaoCardapio: number;
  nome: string;
  idUnidade: number;
  nomeUnidade: string;
  /** Rede a que a loja já pertence (uma loja participa de no máximo uma). */
  idRedeAtual: number | null;
}

export interface LojaDaRede {
  idConfiguracaoCardapio: number;
  nome: string;
  idUnidade: number;
}

export interface PedidoCriado {
  /** UUID do pedido_cardapio. */
  id: string;
  situacao: string;
  shortId?: string | null;
  dataCriacao?: string;
}

/** Resultado de venda TEF (ELGIN) normalizado pelo frontend, enviado ao backend. */
export interface RegistrarTefPayload {
  idPedido: string;
  metodo: 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD';
  aprovado: boolean;
  uniqueId?: string;
  nsu?: string;
  bandeira?: string;
  authorizationCode?: string;
  adquirente?: string;
  dataTransacao?: string;
  /** Comprovante (via cliente) + local de impressão (config local do totem). */
  comprovante?: string;
  localImpressao?: string;
  raw?: unknown;
}

export interface RegistrarPagamentoTotemPayload {
  idPedidoIntegracao: number;
  metodo: 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD';
  /** Forma cadastrada que o cliente escolheu; sem ela o backend lança na primeira que aceita o método. */
  idFormaPagamento?: number | null;
  valorCentavos: number;
  fecharPedido: boolean;
  uniqueId?: string | null;
  nsu?: string | null;
  bandeira?: string | null;
  authorizationCode?: string | null;
  adquirente?: string | null;
  tipoCartao?: string | null;
  dataTransacao?: string | null;
  comprovante?: string | null;
  localImpressao?: string | null;
  imprimeNoAparelho?: boolean;
  raw?: unknown;
}

export type MotivoNaoFechadoTotem =
  | 'VALOR_ZERO'
  | 'VALOR_INSUFICIENTE'
  | 'SOBREPAGAMENTO'
  | 'PEDIDO_FORA_DA_CONTA'
  | 'PEDIDO_BLOQUEADO'
  | 'SISCHEF_NAO_FECHOU';

export interface RespostaRegistrarPagamentoTotem {
  ok: boolean;
  pago: boolean;
  fechamentoRecusado: boolean;
  motivoNaoFechado: MotivoNaoFechadoTotem | null;
  mensagem: string | null;
  pendenteCentavos: number | null;
  saldoRestanteCentavos: number | null;
}

export interface EmitirNotaTotemPayload {
  idPedidoIntegracao: number;
  cpf?: string | null;
  nome?: string | null;
  imprimir: boolean;
  localImpressao?: string | null;
  imprimirComoLinhas?: boolean;
}
export interface ResultadoEmissaoNfce {
  emitida: boolean;
  jaEmitida?: boolean;
  aprovada: boolean;
  sucesso?: boolean;
  impressa?: boolean;
  chave: string | null;
  mensagem: string | null;
}

export type TipoDocumentoTotem = 'SENHA' | 'RESUMO' | 'NFCE';

export interface DocumentoImpressaoTotem {
  identificador?: string;
  descricao?: string;
  conteudo?: { tipo?: string; linhas?: unknown[] } | null;
}

export interface RespostaDocumentosImpressaoTotem {
  documentos: DocumentoImpressaoTotem[];
  situacaoNota?: 'PROCESSANDO' | 'AUTORIZADA' | 'ERRO' | null;
}

/** Dados do PIX devolvidos pelo /pagar (para exibir QR code + copia-e-cola). */
export interface DadosPixCheckout {
  /** Imagem do QR em base64 (sem o prefixo data:). */
  encodedImage?: string | null;
  /** Copia-e-cola. */
  payload: string;
  expiresAt?: string | null;
}

/** Resposta do início de pagamento online (PIX devolve os dados do QR). */
export interface ResultadoPagamentoOnline {
  status: 'PENDING' | 'AUTHORIZED' | 'CONFIRMED' | 'RECEIVED' | 'REFUNDED' | 'CANCELED' | 'FAILED';
  pix?: DadosPixCheckout | null;
  /** Página hospedada da cobrança (Pix via Open Finance — pagar pelo app do banco). */
  invoiceUrl?: string | null;
}

/** Cartão salvo (tokenizado) do cliente — só dados de exibição, nunca o token. */
export interface CartaoSalvo {
  id: number;
  bandeira: string | null;
  last4: string | null;
  nomeImpresso: string | null;
}

/** Subconjunto do payload do pedido usado na tela de detalhes. */
export interface PayloadPedidoDetalhe {
  tipoPedido?: string;
  /** 'RETIRAR_LOCAL' = pedido de delivery em que o cliente optou por retirar. Ausente/null = entrega. */
  tipoDelivery?: string | null;
  descricao?: string | null;
  identificador?: { tipo?: string; numero?: string | null } | null;
  pessoa?: {
    nome?: string;
    telefone?: string;
    endereco?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    municipio?: { nome?: string } | null;
    estado?: { sigla?: string } | null;
  } | null;
  itens?: Array<{
    nome?: string;
    quantidade?: number;
    subItens?: Array<{ nome?: string; quantidade?: number }>;
  }>;
  pagamentos?: Array<{ formaPagamento?: { descricao?: string } }>;
  valorTotal?: number;
  valorTaxaEntrega?: number;
  valorTaxaEntregaOriginal?: number
  valorTaxaConveniencia?: number
  /** Carimbo do DESPACHO pelo Sischef (gravado pelo webhook de status). Presente = "saiu para entrega". */
  despachadoEm?: string | null;
  /** Cashback usado como desconto. Ausente (pedido antigo) ou null = o pedido não usou. */
  cashback?: { valorUsado?: number } | null
}

/** Status público de um pedido — retornado pelo polling em /api/checkout/pedido/:id. */
export interface StatusPedidoPublico {
  id: string;
  shortId: string | null;
  situacao: string;
  tentativasEnvio: number;
  ultimoErroEnvio: string | null;
  dataCriacao: string;
  dataUltimaAtualizacao: string;
  origem: 'MESA_COMANDA' | 'DELIVERY' | 'TOTEM';
  payloadJson: PayloadPedidoDetalhe | null;
  /** Instante-limite (ISO) do pagamento online (PIX) antes do cancelamento por timeout; null se não se aplica. */
  expiraPagamentoEm: string | null;
  /** Pix local com chave cadastrada: código "copia e cola" (o mesmo vai no QR Code). Ausente em backend antigo. */
  pix?: { copiaECola: string } | null;
  /** Com o que o "Pagar agora" reabre a cobrança; ausente (backend antigo) ou null esconde o botão. */
  metodoPagamentoOnline?: 'PIX' | 'CREDIT_CARD' | null;
  /** Status da cobrança online; com CANCELADO, 'CANCELED' quer dizer cancelado antes do pagamento. */
  statusPagamento?: string | null;
}

/** Estado dos "primeiros passos" da loja (checklist de configuração), derivado de dados reais. */
export interface OnboardingLoja {
  /** A loja tem identificador DELIVERY ativo (o passo de entrega só conta nesse caso). */
  fazDelivery: boolean;
  /** Logo + capa configurados. */
  identidade: boolean;
  /** Ao menos 1 horário de funcionamento ativo. */
  horarios: boolean;
  /** Ao menos 1 forma de pagamento ativa. */
  pagamento: boolean;
  /** Ao menos 1 taxa de entrega (bairro ou km); null quando a loja não faz delivery. */
  entrega: boolean | null;
  /** Percentual concluído entre os passos aplicáveis (0–100). */
  percentual: number;
}

/** Linha do histórico "Meus pedidos" do cliente logado (pedido_cardapio da loja atual). */
export interface MeuPedido {
  id: string;
  shortId: string | null;
  origem: 'MESA_COMANDA' | 'DELIVERY' | 'TOTEM';
  situacao: string;
  statusPagamento: string | null;
  dataCriacao: string;
  dataUltimaAtualizacao: string;
  /** Valor total do pedido, extraído do payload (`valorTotal`). */
  total: number | null;
  /** Quantidade de itens do pedido. */
  qtdItens: number;
}

/** Resposta paginada de "Meus pedidos" (LIMIT/OFFSET no banco). */
export interface RespostaMeusPedidos {
  itens: MeuPedido[];
  pagina: number;
  tamanho: number;
  temMais: boolean;
}

export interface PayloadNovoPedido {
  cliente: { nome: string; telefone: string; cpf?: string };
  identificadorSecundarioNumero?: string;
  /** TABLET: número do identificador principal (mesa no modo MESA; comanda no modo COMANDA). */
  identificadorPrincipalNumero?: string;
  observacaoGeral?: string;
  /** Data/hora ISO do agendamento (delivery). Ausente = "assim que possível". */
  horarioAgendamento?: string;
  /** DELIVERY: 'RETIRAR' = cliente retira na loja; 'RECEBER' = entrega no endereço. */
  metodoEntrega?: 'RECEBER' | 'RETIRAR';
  /**
   * TOTEM: 'LOCAL' = come no salão, 'LEVAR' = leva embora. Vira o tipo do pedido no
   * Sischef (INDOOR / TAKEOUT) e é o que o card do PDV mostra ao operador.
   */
  consumoTotem?: ConsumoTotem;
  /** TOTEM: impressora escolhida na configuração local do aparelho (atrás do PIN). */
  localImpressao?: string;
  /** TOTEM Android: o aparelho imprime sozinho, nada vai para a fila de impressão. */
  imprimeNoAparelho?: boolean;
  codigoPager?: number;
  itens: Array<{
    idProduto: number;
    nome: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
    observacao?: string;
    passos?: Array<{
      idProdutoPasso: number;
      descricao?: string;
      /** Contribuição do passo ao preço (por unidade), já com o tipo de cálculo aplicado. */
      valorPasso?: number;
      /** Bloco de adicionais de UMA opção do passo pai (ex.: sabor): o passo pai e a opção dele. */
      idProdutoPassoPai?: number
      idItemPai?: number
      itens: Array<{ idTabelaPrecoItem: number; idProduto: number; nome?: string; quantidade: number; valor: number }>;
    }>;
  }>;
  enderecoEntrega?: {
    cep?: string;
    logradouro: string;
    numero?: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    uf: string;
    referencia?: string;
    latitude?: number;
    longitude?: number;
  };
  /** Mesa/comanda não levam forma de pagamento (acertado com o garçom). */
  formaPagamento?: string;
  /** Id único da forma no cardápio (preferido sobre o codigo, que não é único). */
  idFormaPagamento?: number;
  troco?: number;
  cupom?: string;
  subtotal: number;
  taxaEntrega: number;
  freteComDesconto?: boolean
  desconto: number;
  total: number;
  /** UUID da tentativa de envio: a mesma chave devolve o pedido já gravado, sem duplicar. */
  chaveIdempotencia?: string
  /** Só vai quando o cliente usa cashback: `0` e `null` são recusados pelo backend, então o campo é omitido. */
  valorCashback?: number
}

/** Linha da listagem de pedidos do backoffice (campos derivados do payload). */
/** Origem do pedido — mais grossa que o tipo do ponto: tablet chega como MESA_COMANDA. */
export type OrigemPedidoAdmin = 'MESA_COMANDA' | 'DELIVERY' | 'TOTEM';

export interface PedidoAdminItem {
  id: string;
  shortId: string | null;
  origem: OrigemPedidoAdmin;
  situacao: string;
  statusPagamento: string | null;
  /** ID do pedido no Sischef (pedido_integracao) — chega assíncrono, via webhook. */
  idPedidoIntegracao: number | null;
  /** Sequencial do pedido no Sischef (o "SEQ" das telas / senha do totem). */
  sequencial: number | null;
  tentativasEnvio: number;
  ultimoErroEnvio: string | null;
  dataCriacao: string;
  clienteNome: string | null;
  /** Vem do jsonb como string. */
  valorTotal: string | null;
  formaPagamento: string | null;
}

export interface PedidosAdminPagina {
  itens: PedidoAdminItem[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

/**
 * Filtros da listagem de pedidos. `de`/`ate` vão como INSTANTES ISO: a tela
 * mostra a data no fuso do navegador, então é ela quem converte o dia escolhido
 * no par início/fim — assim o recorte bate com o que aparece na coluna Data.
 */
export interface FiltrosPedidoAdmin {
  origem?: OrigemPedidoAdmin;
  de?: string;
  ate?: string;
  /** Nome do cliente, sequencial do Sischef ou código/id do pedido. */
  busca?: string;
}

export interface ListarPedidosArgs extends FiltrosPedidoAdmin {
  idConfig: number;
  /** 1-based. */
  pagina: number;
  tamanhoPagina: number;
}

/** Canal do recorte: um canal do cardápio ou `'*'` (todos). */
export type CanalRecorte = CanalCardapio | '*';

export type ConsumoRecorte = ConsumoTotem | '*';

/** Item do recorte de catálogo da loja: o que NÃO aparece nela, e onde. */
export interface ItemRecorte {
  tipo: 'GRUPO' | 'PRODUTO' | 'OPCAO';
  idReferencia: number;
  canal: CanalRecorte;
  /** Ponto de acesso; `0` quando a marcação vale para o canal/loja inteira. */
  idIdentificador: number;
  consumo?: ConsumoRecorte;
}

/** Coluna da tela de recorte: um ponto de acesso ou um canal inteiro. */
export interface ColunaRecorte {
  /** `canal:CARDAPIO_X` ou `ponto:123`. */
  chave: string;
  /** Nome do ponto (ex.: "TOTEM 1", "boteco"). */
  rotulo: string;
  /** Tipo do ponto — é por ele que a tela agrupa (ex.: "Delivery", "Totem"). */
  grupo: string;
  canal: CanalCardapio;
  idIdentificador: number;
  consumo: ConsumoRecorte;
  mostraSubPasso?: boolean;
}

export interface OpcaoRecorte {
  id: number;
  descricao: string;
  pausado: boolean;
}

export interface SubPassoRecorte {
  id: number;
  descricao: string;
  opcoes: OpcaoRecorte[];
}

export interface PassoRecorte extends SubPassoRecorte {
  subPasso?: SubPassoRecorte | null;
}

export interface ProdutoRecorte {
  id: number;
  descricao: string;
  /** Pausado no Sischef (ex.: sem estoque): não aparece no cardápio enquanto estiver. */
  pausado: boolean;
  passos?: PassoRecorte[];
}

export interface GrupoRecorte {
  id: number;
  descricao: string;
  produtos: ProdutoRecorte[];
}

export interface RecorteCatalogo {
  /** Pontos de acesso da loja que viram coluna (mesas entram agrupadas). */
  colunas: ColunaRecorte[];
  ocultos: ItemRecorte[];
  grupos: GrupoRecorte[];
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryComRefresh,
  tagTypes: [
    'Usuario',
    'Endereco',
    'Pedido',
    'Loja',
    'AdminMe',
    'AdminConfiguracao',
    'AdminIdentificador',
    'AdminConfigPontoAcesso',
    'AdminCupom',
    'AdminRede',
    'AdminHorario',
    'AdminPausaCanal',
    'AdminFormaPagamento',
    'AdminTaxaBairro',
    'AdminTaxaKm',
    'AdminAvaliacao',
    'AdminMunicipioAtendido',
    'AdminPedido',
    'AdminRecorteCatalogo',
    'CartaoSalvo',
    'Cashback',
  ],
  endpoints: (builder) => ({
    solicitarCodigo: builder.mutation<{ enviado: true; expira_em_min: number }, { email: string }>({
      query: (body) => ({ url: '/auth/solicitar-codigo', method: 'POST', body }),
    }),
    validarCodigo: builder.mutation<ValidarCodigoResposta, { email: string; codigo: string }>({
      query: (body) => ({ url: '/auth/validar-codigo', method: 'POST', body }),
      // 'Cashback': quem entra pode ser outro cliente, e o saldo guardado seria o do anterior.
      invalidatesTags: ['Usuario', 'Cashback'],
    }),
    completarCadastro: builder.mutation<
      SessaoCliente,
      { cadastroToken: string; nome: string; telefone: string; cpf?: string }
    >({
      query: (body) => ({ url: '/auth/completar-cadastro', method: 'POST', body }),
      invalidatesTags: ['Usuario', 'Cashback'],
    }),
    getMe: builder.query<Usuario, void>({
      query: () => '/me',
      providesTags: ['Usuario'],
    }),
    atualizarPerfil: builder.mutation<Usuario, { nome?: string; cpf?: string | null; telefone?: string | null }>({
      query: (body) => ({ url: '/me', method: 'PATCH', body }),
      // 'Cashback': o saldo é achado pelo CPF ou telefone do perfil, então mudar um deles muda a conta encontrada.
      invalidatesTags: ['Usuario', 'Cashback'],
    }),
    getMeuCashback: builder.query<CashbackDoCliente, void>({
      query: () => '/me/cashback',
      providesTags: ['Cashback'],
    }),
    cadastrarNoCashback: builder.mutation<CadastroNoCashback, DadosCadastroCashback>({
      query: (body) => ({ url: '/me/cashback/cadastro', method: 'POST', body }),
      // 'Usuario': o cadastro grava o CPF e o telefone no perfil.
      invalidatesTags: ['Cashback', 'Usuario'],
    }),
    getEnderecos: builder.query<Endereco[], void>({
      query: () => '/me/enderecos',
      providesTags: ['Endereco'],
    }),
    criarEndereco: builder.mutation<Endereco, NovoEndereco>({
      query: (body) => ({ url: '/me/enderecos', method: 'POST', body }),
      invalidatesTags: ['Endereco'],
    }),
    atualizarEndereco: builder.mutation<Endereco, { id: number; dados: Partial<NovoEndereco> }>({
      query: ({ id, dados }) => ({ url: `/me/enderecos/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['Endereco'],
    }),
    removerEndereco: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/me/enderecos/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Endereco'],
    }),
    getLojaAtual: builder.query<LojaAtualResponse, void>({
      query: () => '/loja/atual',
      providesTags: ['Loja'],
    }),
    getResumoAvaliacoesLoja: builder.query<ResumoAvaliacoesLoja, void>({
      query: () => '/loja/avaliacoes',
      providesTags: ['Loja'],
    }),
    getHorariosLoja: builder.query<HorariosLojaResponse, void>({
      query: () => '/loja/horarios',
      providesTags: ['Loja'],
    }),
    getFormasPagamentoLoja: builder.query<FormaPagamentoLoja[], void>({
      query: () => '/loja/formas-pagamento',
      providesTags: ['Loja'],
    }),
    getMunicipiosLoja: builder.query<MunicipioLoja[], void>({
      query: () => '/loja/municipios',
      providesTags: ['Loja'],
    }),
    getBairrosLoja: builder.query<BairroLoja[], void>({
      query: () => '/loja/bairros',
      providesTags: ['Loja'],
    }),
    /** GPS do cliente → endereço preenchido + a coordenada, que fica salva. */
    enderecoPorCoordenada: builder.mutation<EnderecoDaCoordenada, { latitude: number; longitude: number }>({
      query: (body) => ({ url: '/loja/endereco-por-coordenada', method: 'POST', body }),
    }),
    getCatalogo: builder.query<CatalogoResponse, ArgCatalogo>({
      query: (arg) => {
        const { canal, consumo } = typeof arg === 'string' ? { canal: arg, consumo: undefined } : arg;
        return `/catalogo?canal=${canal}${consumo ? `&consumo=${consumo}` : ''}`;
      },
    }),
    // Manda bairro E endereço: a loja escolhe a estratégia (bairro ou km) no
    // backoffice e o backend decide — assim trocar de estratégia não depende de
    // release do app.
    calcularTaxa: builder.mutation<
      TaxaEntregaResponse,
      {
        bairro?: { nome: string; municipio: string; uf: string };
        endereco?: {
          logradouro?: string | null;
          numero?: string | null;
          bairro?: string | null;
          municipio?: string | null;
          uf?: string | null;
          cep?: string | null;
          latitude?: number | null;
          longitude?: number | null;
        };
      }
    >({
      query: (body) => ({ url: '/checkout/taxa-entrega', method: 'POST', body }),
    }),
    criarPedido: builder.mutation<PedidoCriado, PayloadNovoPedido>({
      query: (body) => ({ url: '/checkout/pedido', method: 'POST', body }),
      // 'Cashback' só no sucesso: depois de um erro o saldo não é reconsultado, e o "Tentar de novo" manda o mesmo valor.
      invalidatesTags: (_pedido, erro) => (erro ? ['Pedido'] : ['Pedido', 'Cashback']),
    }),
    registrarPagamentoTef: builder.mutation<{ status: 'CONFIRMED' | 'FAILED' }, RegistrarTefPayload>({
      query: ({ idPedido, ...body }) => ({ url: `/checkout/pedido/${idPedido}/tef`, method: 'POST', body }),
      invalidatesTags: ['Pedido'],
    }),
    pagarPedidoOnline: builder.mutation<
      ResultadoPagamentoOnline,
      {
        idPedido: string;
        metodo: 'PIX' | 'CREDIT_CARD';
        cartao?: { numero: string; nome: string; validadeMes: string; validadeAno: string; cvv: string };
        /** Cobrar com um cartão salvo (token) — sem redigitar nada. */
        idCartaoSalvo?: number;
        /** Tokenizar e salvar o cartão informado p/ a próxima compra (opt-in). */
        salvarCartao?: boolean;
      }
    >({
      query: ({ idPedido, ...body }) => ({ url: `/checkout/pedido/${idPedido}/pagar`, method: 'POST', body }),
      invalidatesTags: ['Pedido', 'CartaoSalvo'],
    }),
    /** O cliente desiste do pagamento online: 422 PEDIDO_NAO_AGUARDA_PAGAMENTO = já foi pago. */
    cancelarPedidoAguardandoPagamento: builder.mutation<{ id: string; situacao: string }, string>({
      query: (idPedido) => ({ url: `/checkout/pedido/${idPedido}/cancelar`, method: 'POST' }),
      // 'Cashback': o cancelamento devolve o saldo usado no pedido antes de responder.
      invalidatesTags: ['Pedido', 'Cashback'],
    }),
    validarPinTotem: builder.mutation<{ ok: boolean }, { pin: string }>({
      query: (body) => ({ url: '/loja/validar-pin-totem', method: 'POST', body }),
    }),
    conferirPagerEmUso: builder.mutation<
      { emUso: boolean; pedido: string | null; desdeMinutos: number | null },
      { numero: number }
    >({
      query: ({ numero }) => ({ url: `/loja/pager-em-uso?numero=${numero}` }),
    }),
    /** Impressoras da loja para o combo da config local do aparelho — atrás do mesmo PIN. */
    listarLocaisImpressaoDispositivo: builder.mutation<LocalImpressaoTablet[], { pin: string }>({
      query: (body) => ({ url: '/loja/locais-impressao', method: 'POST', body }),
    }),
    chamarGarcomTablet: builder.mutation<{ ok: boolean }, { identificacao: string; opcao: string }>({
      query: (body) => ({ url: '/tablet/chamar-garcom', method: 'POST', body }),
    }),
    getContaTablet: builder.query<
      ContaTablet,
      { tipo: 'MESA' | 'COMANDA'; identificador: string; itens?: boolean }
    >({
      query: ({ tipo, identificador, itens }) =>
        `/tablet/conta?tipo=${tipo}&identificador=${encodeURIComponent(identificador)}${itens ? '&itens=true' : ''}`,
      // Conta muda a cada pedido lançado (tablet ou garçom): nunca servir cache.
      keepUnusedDataFor: 0,
    }),
    /**
     * Confere mesa/comanda no cadastro do Sischef. Vale para tablet e QR de celular.
     *
     * `numero` é a grafia do cadastro, que pode diferir da digitada ('001' numa
     * loja que cadastrou '1'). Para o Sischef as duas são contas diferentes, então
     * a tela troca o que o cliente digitou por esta — quem grava é o backend, que
     * normaliza de novo.
     */
    validarIdentificadorSalao: builder.query<
      { valida: boolean; numero?: string },
      { identificador: string; tipo: 'MESA' | 'COMANDA' }
    >({
      query: ({ identificador, tipo }) =>
        `/tablet/validar-identificador?identificador=${encodeURIComponent(identificador)}&tipo=${tipo}`,
    }),
    /** Traduz o conteúdo lido de um QR para { tipo, numero }. 404 = não é desta loja.
     *  `tipo` diz como ler um QR com o número puro, que não traz o tipo. */
    resolverIdentificadorTablet: builder.query<
      { tipo: 'MESA' | 'COMANDA'; numero: string },
      { valor: string; tipo: 'MESA' | 'COMANDA' }
    >({
      query: ({ valor, tipo }) =>
        `/tablet/resolver-identificador?valor=${encodeURIComponent(valor)}&tipo=${tipo}`,
    }),
    /**
     * Traduz a ETIQUETA ASSINADA de mesa/comanda no código do ponto de acesso.
     *
     * É o link do cupom da BALANÇA de autoatendimento
     * (`/entrada/SCF1.C.5.aB3dEf9k2Q`): na impressão a retaguarda só sabe o
     * número, e o QR do cardápio carrega código opaco. A etiqueta é assinada
     * (mesmo formato que o tablet lê pela câmera) porque número cru de comanda
     * é adivinhável — sem assinatura, qualquer um lançava pedido na conta do
     * vizinho pelo link público da loja. A etiqueta é opaca aqui: quem confere
     * a assinatura é o servidor. A loja vem do host (X-Tenant); 404 = etiqueta
     * inválida, de outra unidade, ou mesa/comanda sem ponto de acesso ativo.
     */
    resolverEntradaPorEtiqueta: builder.query<{ codigo: string }, { etiqueta: string }>({
      query: ({ etiqueta }) => `/entrada/${encodeURIComponent(etiqueta)}`,
    }),
    getContaTotemCheckout: builder.query<ContaTablet, { identificador: string }>({
      query: ({ identificador }) => `/totem-checkout/conta?identificador=${encodeURIComponent(identificador)}`,
    }),
    adicionarItensComanda: builder.mutation<
      { id?: string; idPedidoIntegracao?: number; duplicado?: boolean },
      {
        identificador: string;
        itens: ItemAdicionarComanda[];
        idPedidoIntegracao?: number;
        chaveIdempotencia?: string;
      }
    >({
      query: (body) => ({ url: '/totem-checkout/adicionar-itens', method: 'POST', body }),
    }),
    alterarTaxaServicoTotem: builder.mutation<
      ResultadoTaxaServicoTotem,
      { idPedidoIntegracao: number; acao: 'SINCRONIZAR' | 'DISPENSAR' | 'REAPLICAR' }
    >({
      query: (body) => ({ url: '/totem-checkout/taxa-servico', method: 'POST', body }),
    }),
    registrarPagamentoTotem: builder.mutation<RespostaRegistrarPagamentoTotem, RegistrarPagamentoTotemPayload>({
      query: (body) => ({ url: '/totem-checkout/registrar-pagamento', method: 'POST', body }),
    }),
    emitirNotaTotem: builder.mutation<ResultadoEmissaoNfce, EmitirNotaTotemPayload>({
      query: (body) => ({ url: '/totem-checkout/emitir-nota', method: 'POST', body }),
    }),
    /** Imprime o resumo da conta pelo Sischef — caminho de quem não emite NFC-e no totem. */
    imprimirResumoTotem: builder.mutation<
      { impressa: boolean; mensagem: string | null },
      { idPedidoIntegracao: number; localImpressao?: string | null }
    >({
      query: (body) => ({ url: '/totem-checkout/imprimir-resumo', method: 'POST', body }),
    }),
    /** TOTEM Android: documento pronto (resumo ou NFC-e) para sair na impressora do aparelho. */
    conteudoImpressaoTotem: builder.mutation<
      RespostaDocumentosImpressaoTotem,
      { idPedidoIntegracao: number; documento: 'RESUMO' | 'NFCE' }
    >({
      query: (body) => ({ url: '/totem-checkout/conteudo-impressao', method: 'POST', body }),
    }),
    /** TOTEM Android (pedidos): senha, resumo ou NFC-e prontos. Lista vazia = ainda não disponível. */
    getDocumentoImpressaoTotem: builder.query<
      RespostaDocumentosImpressaoTotem,
      { idPedido: string; documento: TipoDocumentoTotem }
    >({
      query: ({ idPedido, documento }) => `/checkout/pedido/${idPedido}/impressao/${documento}`,
      keepUnusedDataFor: 0,
    }),
    validarCupom: builder.mutation<CupomValidado, { cupom: string; subtotal: number; metodoEntrega?: PayloadNovoPedido['metodoEntrega'] }>({
      query: (body) => ({ url: '/checkout/validar-cupom', method: 'POST', body }),
    }),
    getPedidoStatus: builder.query<StatusPedidoPublico, string>({
      query: (id) => `/checkout/pedido/${id}`,
      providesTags: ['Pedido'],
    }),
    /**
     * Senha do totem (= sequencial do Sischef) e acompanhamento da NFC-e. Ambos `null`
     * até o Java processar a fila; `notaFiscal` segue `null` em lojas que não emitem
     * nota no totem e em Sischef sem o acompanhamento fiscal no getHook.
     */
    getSenhaTotem: builder.query<SenhaTotem, string>({
      query: (id) => `/checkout/pedido/${id}/senha`,
    }),
    /** Nota em "Perguntar ao cliente": imprime a NFC-e já emitida. Cada chamada é uma nota no papel. */
    imprimirNotaTotem: builder.mutation<
      ResultadoImpressaoNotaTotem,
      { id: string; localImpressao?: string | null }
    >({
      query: ({ id, ...body }) => ({ url: `/checkout/pedido/${id}/imprimir-nota`, method: 'POST', body }),
    }),
    getMeusPedidos: builder.query<RespostaMeusPedidos, number | void>({
      query: (pagina) => `/me/pedidos?pagina=${pagina ?? 1}`,
      providesTags: ['Pedido'],
    }),
    getCartoesSalvos: builder.query<CartaoSalvo[], void>({
      query: () => '/me/cartoes',
      providesTags: ['CartaoSalvo'],
    }),
    excluirCartaoSalvo: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/me/cartoes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['CartaoSalvo'],
    }),
    getAvaliacao: builder.query<
      {
        pedido: { id: string; situacao: string; clienteNome: string | null };
        avaliacao: { id: number; estrelas: number; comentario: string | null } | null;
      },
      string
    >({
      query: (idPedido) => `/avaliacao/${idPedido}`,
    }),
    enviarAvaliacao: builder.mutation<
      { id: number },
      { idPedido: string; estrelas: number; comentario?: string }
    >({
      query: ({ idPedido, ...body }) => ({ url: `/avaliacao/${idPedido}`, method: 'POST', body }),
    }),

    // ===== ADMIN =====
    adminMe: builder.query<
      {
        id: number;
        username: string;
        nome: string | null;
        sobrenome: string | null;
        email: string | null;
        idUnidade: number | null;
        nomeUnidade: string | null;
        /** True se o admin logado tem ROLE_ADMIN no Sischef. */
        roleAdmin: boolean;
        /** True se a empresa tem o módulo "Cardápio Sischef" (ROLE_CARDAPIO_SISCHEF). */
        temAcessoCardapio: boolean;
        /** True se a empresa tem o módulo de Cashback (ROLE_CASHBACK_CONFIG) — libera o switch na configuração. */
        temModuloCashback: boolean
        /** Modos contratados pela empresa — gateiam as abas de modo na configuração. */
        modos: AcessoModos;
        /**
         * Empresa é a MATRIZ de uma franquia (ROLE_FRANQUEADOR): monta a rede de
         * lojas e cria cupom de escopo REDE. Com `false`, o front não mostra NADA
         * de rede — a tela de cupom fica idêntica à de antes da feature.
         */
        redeGerenciavel: boolean;
        /** Matriz OU franqueada: enxerga cupom de rede na lista (somente leitura). */
        redeVisivel: boolean;
        /** Usuário SUPORTE: vê lojas excluídas e pode restaurá-las. */
        suporte: boolean;
        /** SUPERVISOR ou SUPORTE: pode excluir a loja. */
        podeExcluirLoja: boolean;
      },
      void
    >({
      query: () => '/admin/me',
      providesTags: ['AdminMe'],
    }),
    /**
     * Solicita ao Sischef a liberação de um módulo do cardápio (envia e-mail pro
     * comercial). Sem `role`, pede o módulo geral (ROLE_CARDAPIO_SISCHEF); com
     * `role`, pede o modo específico (ex.: ROLE_CARDAPIO_TOTEM).
     */
    adminSolicitarAcesso: builder.mutation<{ ok: true }, { role?: string } | void>({
      query: (arg) => ({
        url: '/admin/solicitar-acesso',
        method: 'POST',
        body: arg && 'role' in arg ? { role: arg.role } : {},
      }),
    }),
    adminListarConfiguracoes: builder.query<ConfiguracaoLoja[], void>({
      query: () => '/admin/configuracoes',
      providesTags: ['AdminConfiguracao'],
    }),
    adminObterConfiguracao: builder.query<ConfiguracaoLoja & Record<string, unknown>, number>({
      query: (id) => `/admin/configuracoes/${id}`,
      providesTags: ['AdminConfiguracao'],
    }),
    adminObterOnboarding: builder.query<OnboardingLoja, number>({
      query: (id) => `/admin/configuracoes/${id}/onboarding`,
      // Reflete todos os sinais do checklist — configurar qualquer passo (logo/capa,
      // identificador, horário, forma de pagamento, taxa) reavalia o progresso e os badges.
      providesTags: [
        'AdminConfiguracao',
        'AdminIdentificador',
        'AdminHorario',
        'AdminFormaPagamento',
        'AdminTaxaBairro',
        'AdminTaxaKm',
      ],
    }),
    /** Impressoras da unidade — pra escolher a do "chamar garçom" do tablet (config global). */
    adminListarLocaisImpressao: builder.query<LocalImpressaoTablet[], number>({
      query: (id) => `/admin/configuracoes/${id}/locais-impressao`,
    }),
    /** Usuários da unidade que podem assinar a NFC-e do totem (sem SUPORTE/REPRESENTANTE). */
    adminListarUsuariosFiscais: builder.query<UsuarioEmissorNfce[], number>({
      query: (id) => `/admin/configuracoes/${id}/usuarios-fiscais`,
    }),
    /** Se o programa de cashback da loja está ligado e o limite de uso por pedido, só para mostrar. */
    adminObterProgramaCashback: builder.query<ProgramaCashbackLoja, number>({
      query: (id) => `/admin/configuracoes/${id}/cashback-programa`,
    }),
    adminCriarConfiguracao: builder.mutation<ConfiguracaoLoja, Record<string, unknown>>({
      query: (body) => ({ url: '/admin/configuracoes', method: 'POST', body }),
      // Cria também o identificador DELIVERY (subdomínio), então invalida ambos.
      invalidatesTags: ['AdminConfiguracao', 'AdminIdentificador'],
    }),
    adminAtualizarConfiguracao: builder.mutation<unknown, { id: number; dados: Record<string, unknown> }>({
      query: ({ id, dados }) => ({ url: `/admin/configuracoes/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminConfiguracao'],
    }),
    // Exclusão e restauração também mexem nos pontos de acesso e na lista de lojas da rede.
    adminExcluirConfiguracao: builder.mutation<{ ok: true }, number>({
      query: (id) => ({ url: `/admin/configuracoes/${id}/excluir`, method: 'POST' }),
      invalidatesTags: ['AdminConfiguracao', 'AdminIdentificador', 'AdminRede'],
    }),
    adminRestaurarConfiguracao: builder.mutation<{ ok: true; enderecosNaoRecuperados: string[] }, number>({
      query: (id) => ({ url: `/admin/configuracoes/${id}/restaurar`, method: 'POST' }),
      invalidatesTags: ['AdminConfiguracao', 'AdminIdentificador', 'AdminRede'],
    }),
    adminInvalidarCacheConfiguracao: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/admin/configuracoes/${id}/invalidar-cache`, method: 'POST' }),
    }),
    adminObterEnderecoEmpresa: builder.query<EnderecoEmpresaSischef, number>({
      query: (id) => `/admin/configuracoes/${id}/endereco-empresa`,
    }),

    adminListarIdentificadores: builder.query<
      Paginado<IdentificadorLoja>,
      ListarPaginadoArgs & { tipos?: TipoIdentificador[] }
    >({
      // `tipos` recorta a listagem por modo — é o que faz o modal de "Pontos de
      // acesso" de cada aba mostrar só os pontos daquela aba. Sem ele, a tela
      // de acessos lista a loja inteira.
      query: ({ tipos, ...args }) =>
        `/admin/identificadores?${qsPaginacao(args)}` +
        (tipos?.length ? `&tipos=${encodeURIComponent(tipos.join(','))}` : ''),
      providesTags: ['AdminIdentificador'],
    }),
    adminCriarIdentificador: builder.mutation<
      IdentificadorLoja,
      { idConfiguracaoCardapio: number; tipo: string; numero?: string; codigo?: string; dominioCompleto?: boolean }
    >({
      query: (body) => ({ url: '/admin/identificadores', method: 'POST', body }),
      invalidatesTags: ['AdminIdentificador'],
    }),
    adminCriarIdentificadoresEmFaixa: builder.mutation<
      /** `naoCadastradas`: comandas da faixa que não existem no Sischef (foram puladas). */
      { criados: number; pulados: number; naoCadastradas: string[] },
      { idConfiguracaoCardapio: number; tipo: 'MESA' | 'COMANDA'; numeroInicial: number; numeroFinal: number }
    >({
      query: (body) => ({ url: '/admin/identificadores/faixa', method: 'POST', body }),
      invalidatesTags: ['AdminIdentificador'],
    }),
    adminAtualizarIdentificador: builder.mutation<
      IdentificadorLoja,
      { id: number; dados: { ativo?: boolean; numero?: string; codigo?: string; tipo?: string; dominioCompleto?: boolean } }
    >({
      query: ({ id, dados }) => ({ url: `/admin/identificadores/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminIdentificador'],
    }),
    adminRemoverIdentificador: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/identificadores/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminIdentificador'],
    }),
    adminGerarCodigoPareamento: builder.mutation<
      { codigo: string; dataExpiracao: string; validadeMin: number },
      number
    >({
      query: (id) => ({ url: `/admin/identificadores/${id}/pareamento`, method: 'POST' }),
    }),
    adminStatusPareamento: builder.query<
      { vinculado: boolean; dataUso: string | null; fingerprint: string | null; ipUso: string | null },
      number
    >({
      query: (id) => `/admin/identificadores/${id}/pareamento`,
      providesTags: ['AdminIdentificador'],
    }),
    adminDesvincularAparelho: builder.mutation<{ ok: boolean }, number>({
      query: (id) => ({ url: `/admin/identificadores/${id}/pareamento`, method: 'DELETE' }),
      invalidatesTags: ['AdminIdentificador'],
    }),
    resgatarPareamento: builder.mutation<
      { slug: string; tipo: TipoIdentificador; numero: string | null; nomeLoja: string; codigoTroca: string },
      { codigo: string }
    >({
      query: (body) => ({ url: '/pareamento/resgatar', method: 'POST', body }),
    }),
    adminVerificarSubdominio: builder.query<{ disponivel: boolean; mensagem: string | null }, string>({
      query: (codigo) => `/admin/identificadores/disponivel?codigo=${encodeURIComponent(codigo)}`,
    }),
    adminResumoIdentificadores: builder.query<{ resumo: ResumoIdentificadores }, number>({
      query: (idConfig) => `/admin/identificadores/resumo?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminIdentificador'],
    }),
    adminListarConfigPontosAcesso: builder.query<
      { pontos: PontoAcessoConfiguravel[] },
      { idConfig: number; tipo: TipoIdentificador }
    >({
      query: ({ idConfig, tipo }) =>
        `/admin/config-ponto-acesso?idConfiguracaoCardapio=${idConfig}&tipo=${encodeURIComponent(tipo)}`,
      providesTags: ['AdminConfigPontoAcesso', 'AdminIdentificador'],
    }),
    adminSalvarConfigPontoAcesso: builder.mutation<
      { id: number; configuracao: ConfigPontoAcessoOverride | null },
      { id: number; configuracao: ConfigPontoAcessoOverride }
    >({
      query: ({ id, configuracao }) => ({
        url: `/admin/config-ponto-acesso/${id}`,
        method: 'PUT',
        body: { configuracao },
      }),
      invalidatesTags: ['AdminConfigPontoAcesso'],
    }),
    adminDuplicarConfigPontoAcesso: builder.mutation<
      { atualizados: number },
      { idOrigem: number; idsDestino: number[]; chaves?: ChaveConfigPontoAcesso[] }
    >({
      query: (body) => ({ url: '/admin/config-ponto-acesso/duplicar', method: 'POST', body }),
      invalidatesTags: ['AdminConfigPontoAcesso'],
    }),
    /** Todos os identificadores ativos dos tipos dados (sem paginação) — p/ baixar os QR em ZIP. */
    adminExportarIdentificadores: builder.query<
      { itens: IdentificadorLoja[]; limite: number },
      { idConfig: number; tipos: TipoIdentificador[] }
    >({
      query: ({ idConfig, tipos }) =>
        `/admin/identificadores/exportar?idConfiguracaoCardapio=${idConfig}&tipos=${encodeURIComponent(tipos.join(','))}`,
    }),

    /** Comandas cadastradas no Sischef, com o conteúdo do QR (o número) pronto. */
    adminListarComandasSischef: builder.query<
      { comandas: Array<{ numero: string; etiqueta: string }>; truncado: boolean; limite: number },
      { idConfig: number; de?: string; ate?: string }
    >({
      query: ({ idConfig, de, ate }) => {
        const p = new URLSearchParams({ idConfiguracaoCardapio: String(idConfig) });
        if (de?.trim()) p.set('de', de.trim());
        if (ate?.trim()) p.set('ate', ate.trim());
        return `/admin/identificadores/sischef?${p.toString()}`;
      },
    }),

    adminListarCupons: builder.query<Paginado<unknown>, ListarPaginadoArgs>({
      query: (args) => `/admin/cupons?${qsPaginacao(args)}`,
      providesTags: ['AdminCupom'],
    }),
    adminCriarCupom: builder.mutation<unknown, Record<string, unknown>>({
      query: (body) => ({ url: '/admin/cupons', method: 'POST', body }),
      invalidatesTags: ['AdminCupom'],
    }),
    adminAtualizarCupom: builder.mutation<unknown, { id: number; dados: Record<string, unknown> }>({
      query: ({ id, dados }) => ({ url: `/admin/cupons/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminCupom'],
    }),
    adminRemoverCupom: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/cupons/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminCupom'],
    }),

    // ===== REDE DE LOJAS (só matriz de franquia — ver docs/specs/cupom-rede.md) =====
    adminListarRedes: builder.query<RedeCardapio[], void>({
      query: () => '/admin/redes',
      providesTags: ['AdminRede'],
    }),
    adminListarLojasCandidatasRede: builder.query<{ idFranquia: number; lojas: LojaCandidataRede[] }, void>({
      query: () => '/admin/redes/lojas-candidatas',
      providesTags: ['AdminRede'],
    }),
    adminCriarRede: builder.mutation<RedeCardapio, { nome: string }>({
      query: (body) => ({ url: '/admin/redes', method: 'POST', body }),
      invalidatesTags: ['AdminRede'],
    }),
    adminAtualizarRede: builder.mutation<RedeCardapio, { id: number; dados: { nome?: string; ativo?: boolean } }>({
      query: ({ id, dados }) => ({ url: `/admin/redes/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminRede'],
    }),
    adminListarLojasDaRede: builder.query<LojaDaRede[], number>({
      query: (id) => `/admin/redes/${id}/lojas`,
      providesTags: ['AdminRede'],
    }),
    adminSalvarLojasDaRede: builder.mutation<LojaDaRede[], { id: number; lojas: number[] }>({
      query: ({ id, lojas }) => ({ url: `/admin/redes/${id}/lojas`, method: 'PUT', body: { lojas } }),
      // Mexer nas lojas muda quais cupons de rede cada loja enxerga.
      invalidatesTags: ['AdminRede', 'AdminCupom'],
    }),

    adminListarHorarios: builder.query<HorarioFuncionamentoLoja[], number>({
      query: (idConfig) => `/admin/horarios-funcionamento?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminHorario'],
    }),
    adminCriarHorario: builder.mutation<
      HorarioFuncionamentoLoja,
      {
        idConfiguracaoCardapio: number;
        /** Ausente/null = faixa base; canal = override daquele canal. */
        canal?: CanalCardapio | null;
        diaSemana: number;
        horaInicio: string;
        horaFim: string;
      }
    >({
      query: (body) => ({ url: '/admin/horarios-funcionamento', method: 'POST', body }),
      invalidatesTags: ['AdminHorario'],
    }),
    adminAtualizarHorario: builder.mutation<
      HorarioFuncionamentoLoja,
      { id: number; dados: Partial<Pick<HorarioFuncionamentoLoja, 'diaSemana' | 'horaInicio' | 'horaFim' | 'ativo'>> }
    >({
      query: ({ id, dados }) => ({ url: `/admin/horarios-funcionamento/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminHorario'],
    }),
    adminRemoverHorario: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/horarios-funcionamento/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminHorario'],
    }),

    // ===== Pausa manual por canal (liga/desliga em tempo real) =====
    adminListarPausas: builder.query<{ pausasCanal: PausasCanal }, number>({
      query: (idConfig) => `/admin/pausa-canal?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminPausaCanal'],
    }),
    adminPausarCanal: builder.mutation<
      { pausasCanal: PausasCanal },
      { idConfiguracaoCardapio: number; canal: CanalCardapio; pausado: boolean }
    >({
      query: (body) => ({ url: '/admin/pausa-canal', method: 'PUT', body }),
      invalidatesTags: ['AdminPausaCanal'],
    }),

    // ===== Formas de pagamento =====
    adminListarFormasPagamento: builder.query<Paginado<FormaPagamentoLoja>, ListarPaginadoArgs>({
      query: (args) => `/admin/formas-pagamento?${qsPaginacao(args)}`,
      providesTags: ['AdminFormaPagamento'],
    }),
    adminCriarFormaPagamento: builder.mutation<
      FormaPagamentoLoja,
      {
        idConfiguracaoCardapio: number;
        tipo: TipoPagamentoLoja;
        forma: FormaPagamentoOpcao;
        codigo: string;
        descricao: string;
        idConfiguracaoIntegracao?: number | null;
        metodos?: string | null;
        visibilidadeFlags?: string | null;
        pixChave?: string | null;
        pixNomeRecebedor?: string | null;
        pixCidade?: string | null;
        ordem?: number;
        ativo?: boolean;
      }
    >({
      query: (body) => ({ url: '/admin/formas-pagamento', method: 'POST', body }),
      invalidatesTags: ['AdminFormaPagamento'],
    }),
    adminAtualizarFormaPagamento: builder.mutation<
      FormaPagamentoLoja,
      {
        id: number;
        dados: Partial<
          Pick<
            FormaPagamentoLoja,
            | 'tipo'
            | 'forma'
            | 'codigo'
            | 'descricao'
            | 'idConfiguracaoIntegracao'
            | 'metodos'
            | 'visibilidadeFlags'
            | 'pixChave'
            | 'pixNomeRecebedor'
            | 'pixCidade'
            | 'ordem'
            | 'ativo'
          >
        >;
      }
    >({
      query: ({ id, dados }) => ({ url: `/admin/formas-pagamento/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminFormaPagamento'],
    }),
    adminListarFormasPagamentoSischef: builder.query<FormaPagamentoSischef[], void>({
      query: () => '/admin/formas-pagamento/sischef',
    }),
    adminListarIntegracoesPagamento: builder.query<
      IntegracaoPagamentoOpcao[],
      { idConfig: number; tipo: 'ONLINE' | 'TEF' }
    >({
      query: ({ idConfig, tipo }) =>
        `/admin/formas-pagamento/integracoes?idConfiguracaoCardapio=${idConfig}&tipo=${tipo}`,
    }),
    adminWebhookAsaas: builder.query<WebhookAsaasInfo, { idConfig: number }>({
      query: ({ idConfig }) => `/admin/formas-pagamento/webhook-asaas?idConfiguracaoCardapio=${idConfig}`,
    }),
    adminDiagnosticoPix: builder.query<
      DiagnosticoPixConta,
      { idConfig: number; idConfiguracaoIntegracao: number }
    >({
      query: ({ idConfig, idConfiguracaoIntegracao }) =>
        `/admin/formas-pagamento/diagnostico-pix?idConfiguracaoCardapio=${idConfig}` +
        `&idConfiguracaoIntegracao=${idConfiguracaoIntegracao}`,
    }),
    adminRemoverFormaPagamento: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/formas-pagamento/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminFormaPagamento'],
    }),

    // ===== Taxa de entrega por BAIRRO =====
    adminListarTaxasBairro: builder.query<Paginado<TaxaEntregaBairroLoja>, ListarPaginadoArgs>({
      query: (args) => `/admin/taxa-entrega/bairro?${qsPaginacao(args)}`,
      providesTags: ['AdminTaxaBairro'],
    }),
    adminCriarTaxaBairro: builder.mutation<
      TaxaEntregaBairroLoja,
      { idConfiguracaoCardapio: number; nomeBairro: string; municipio: string; uf: string; valor: number; ativo?: boolean }
    >({
      query: (body) => ({ url: '/admin/taxa-entrega/bairro', method: 'POST', body }),
      invalidatesTags: ['AdminTaxaBairro'],
    }),
    adminAtualizarTaxaBairro: builder.mutation<
      TaxaEntregaBairroLoja,
      { id: number; dados: { nomeBairro?: string; municipio?: string; uf?: string; valor?: number; ativo?: boolean } }
    >({
      query: ({ id, dados }) => ({ url: `/admin/taxa-entrega/bairro/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminTaxaBairro'],
    }),
    adminRemoverTaxaBairro: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/taxa-entrega/bairro/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminTaxaBairro'],
    }),
    adminListarTaxasImportaveisSischef: builder.query<ImportarTaxasSischefResposta, number>({
      query: (idConfig) => `/admin/taxa-entrega/bairro/importar-sischef?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminTaxaBairro'],
    }),
    adminImportarTaxasSischef: builder.mutation<
      { importados: number; atualizados: number; municipio: string; uf: string },
      { idConfiguracaoCardapio: number; idsProduto: number[] }
    >({
      query: (body) => ({ url: '/admin/taxa-entrega/bairro/importar-sischef', method: 'POST', body }),
      invalidatesTags: ['AdminTaxaBairro'],
    }),

    // ===== Taxa de entrega por KM (faixas escalonadas) =====
    /** A cobrança por distância só é liberada com a integração Google Maps
     * funcionando — este endpoint testa a chave de verdade e explica o que
     * fazer quando não está. */
    adminDiagnosticoKm: builder.query<DiagnosticoTaxaKm, { idConfig: number; forcar?: boolean }>({
      query: ({ idConfig, forcar }) =>
        `/admin/taxa-entrega/km/diagnostico?idConfiguracaoCardapio=${idConfig}${forcar ? '&forcar=true' : ''}`,
      // `forcar` é uma ORDEM ao servidor (invalida o cache de 2 min do
      // diagnóstico), não um recorte diferente de dado. Sem fixar a chave, o
      // "testar de novo" caía numa entrada paralela do cache e a tela seguia
      // mostrando o diagnóstico velho — o gestor configurava a chave no Sischef
      // e continuava vendo "indisponível".
      serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}(${queryArgs.idConfig})`,
      providesTags: ['AdminTaxaKm'],
    }),
    adminListarTaxasKm: builder.query<Paginado<TaxaEntregaKmLoja>, ListarPaginadoArgs>({
      query: (args) => `/admin/taxa-entrega/km?${qsPaginacao(args)}`,
      providesTags: ['AdminTaxaKm'],
    }),
    adminCriarTaxaKm: builder.mutation<
      TaxaEntregaKmLoja,
      { idConfiguracaoCardapio: number; kmFinal: number; valor: number; ativo?: boolean }
    >({
      query: (body) => ({ url: '/admin/taxa-entrega/km', method: 'POST', body }),
      invalidatesTags: ['AdminTaxaKm'],
    }),
    adminAtualizarTaxaKm: builder.mutation<
      TaxaEntregaKmLoja,
      { id: number; dados: { kmFinal?: number; valor?: number; ativo?: boolean } }
    >({
      query: ({ id, dados }) => ({ url: `/admin/taxa-entrega/km/${id}`, method: 'PATCH', body: dados }),
      invalidatesTags: ['AdminTaxaKm'],
    }),
    adminRemoverTaxaKm: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/taxa-entrega/km/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminTaxaKm'],
    }),

    // ===== Avaliações (read-mostly no admin) =====
    adminListarAvaliacoes: builder.query<AvaliacoesAdminPagina, ListarPaginadoArgs>({
      query: (args) => `/admin/avaliacoes?${qsPaginacao(args)}`,
      providesTags: ['AdminAvaliacao'],
    }),
    adminRemoverAvaliacao: builder.mutation<unknown, number>({
      query: (id) => ({ url: `/admin/avaliacoes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminAvaliacao'],
    }),

    // ===== Pedidos (admin) =====
    adminListarPedidos: builder.query<PedidosAdminPagina, ListarPedidosArgs>({
      query: ({ idConfig, pagina, tamanhoPagina, origem, de, ate, busca }) => {
        const qs = new URLSearchParams(qsPaginacao({ idConfig, pagina, tamanhoPagina }));
        if (origem) qs.set('origem', origem);
        if (de) qs.set('de', de);
        if (ate) qs.set('ate', ate);
        if (busca) qs.set('busca', busca);
        return `/admin/pedidos?${qs.toString()}`;
      },
      providesTags: ['AdminPedido'],
    }),
    adminReenviarPedido: builder.mutation<
      { ok: boolean; id: string; situacao: string; tentativasEnvio: number },
      string
    >({
      query: (idPedido) => ({ url: `/admin/pedidos/${idPedido}/reenviar`, method: 'POST' }),
      invalidatesTags: ['AdminPedido'],
    }),
    adminForcarIntegracao: builder.mutation<
      { ok: boolean; id: string; situacao: string; tentativasEnvio: number },
      string
    >({
      query: (idPedido) => ({ url: `/admin/pedidos/${idPedido}/forcar-integracao`, method: 'POST' }),
      invalidatesTags: ['AdminPedido'],
    }),

    // ===== Municípios atendidos (vínculo) + catálogo master =====
    adminListarMunicipiosAtendidos: builder.query<MunicipioAtendidoLoja[], number>({
      query: (idConfig) => `/admin/municipios-atendidos?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminMunicipioAtendido'],
    }),
    adminAdicionarMunicipioAtendido: builder.mutation<
      MunicipioAtendidoLoja,
      { idConfiguracaoCardapio: number; idMunicipio: number }
    >({
      query: (body) => ({ url: '/admin/municipios-atendidos', method: 'POST', body }),
      invalidatesTags: ['AdminMunicipioAtendido'],
    }),
    adminRemoverMunicipioAtendido: builder.mutation<
      unknown,
      { idConfiguracaoCardapio: number; idMunicipio: number }
    >({
      query: (body) => ({ url: '/admin/municipios-atendidos', method: 'DELETE', body }),
      invalidatesTags: ['AdminMunicipioAtendido'],
    }),
    adminBuscarMunicipios: builder.query<MunicipioMaster[], { busca?: string; uf?: string; limite?: number }>({
      query: ({ busca, uf, limite }) => {
        const qs = new URLSearchParams();
        if (busca) qs.set('busca', busca);
        if (uf) qs.set('uf', uf);
        if (limite) qs.set('limite', String(limite));
        return `/admin/municipios${qs.toString() ? `?${qs}` : ''}`;
      },
    }),

    // ===== Recorte do catálogo por loja (multi-loja) =====
    adminListarRecorteCatalogo: builder.query<RecorteCatalogo, number>({
      query: (idConfig) => `/admin/catalogo?idConfiguracaoCardapio=${idConfig}`,
      providesTags: ['AdminRecorteCatalogo'],
    }),
    adminSalvarRecorteCatalogo: builder.mutation<
      { ocultos: number },
      { idConfiguracaoCardapio: number; ocultos: ItemRecorte[] }
    >({
      query: (body) => ({ url: '/admin/catalogo', method: 'PUT', body }),
      invalidatesTags: ['AdminRecorteCatalogo'],
    }),

    // ===== Storage (presigned PUT pra upload direto pro S3) =====
    adminGerarPresignedUpload: builder.mutation<
      { uploadUrl: string; key: string; urlFinal: string; expiraEmSeg: number },
      { fileName: string; fileType: string; finalidade: 'logo' | 'capa' | 'produto' | 'avatar' | 'outros' }
    >({
      query: (body) => ({ url: '/admin/storage/presigned-upload', method: 'POST', body }),
    }),
  }),
});

export const {
  useSolicitarCodigoMutation,
  useValidarCodigoMutation,
  useCompletarCadastroMutation,
  useGetMeQuery,
  useAtualizarPerfilMutation,
  useGetMeuCashbackQuery,
  useCadastrarNoCashbackMutation,
  useGetEnderecosQuery,
  useCriarEnderecoMutation,
  useAtualizarEnderecoMutation,
  useRemoverEnderecoMutation,
  useGetLojaAtualQuery,
  useGetResumoAvaliacoesLojaQuery,
  useGetHorariosLojaQuery,
  useGetFormasPagamentoLojaQuery,
  useGetMunicipiosLojaQuery,
  useGetBairrosLojaQuery,
  useGetCatalogoQuery,
  useCalcularTaxaMutation,
  useEnderecoPorCoordenadaMutation,
  useCriarPedidoMutation,
  useRegistrarPagamentoTefMutation,
  usePagarPedidoOnlineMutation,
  useCancelarPedidoAguardandoPagamentoMutation,
  useValidarPinTotemMutation,
  useConferirPagerEmUsoMutation,
  useListarLocaisImpressaoDispositivoMutation,
  useChamarGarcomTabletMutation,
  useLazyGetContaTabletQuery,
  useLazyValidarIdentificadorSalaoQuery,
  useLazyResolverIdentificadorTabletQuery,
  useResolverEntradaPorEtiquetaQuery,
  useLazyGetContaTotemCheckoutQuery,
  useAdicionarItensComandaMutation,
  useAlterarTaxaServicoTotemMutation,
  useRegistrarPagamentoTotemMutation,
  useEmitirNotaTotemMutation,
  useImprimirResumoTotemMutation,
  useConteudoImpressaoTotemMutation,
  useValidarCupomMutation,
  useGetPedidoStatusQuery,
  useGetSenhaTotemQuery,
  useImprimirNotaTotemMutation,
  useAdminListarUsuariosFiscaisQuery,
  useAdminObterProgramaCashbackQuery,
  useGetMeusPedidosQuery,
  useGetCartoesSalvosQuery,
  useExcluirCartaoSalvoMutation,
  useGetAvaliacaoQuery,
  useEnviarAvaliacaoMutation,
  useAdminMeQuery,
  useAdminSolicitarAcessoMutation,
  useAdminListarConfiguracoesQuery,
  useAdminObterConfiguracaoQuery,
  useAdminObterOnboardingQuery,
  useAdminListarLocaisImpressaoQuery,
  useAdminCriarConfiguracaoMutation,
  useAdminAtualizarConfiguracaoMutation,
  useAdminExcluirConfiguracaoMutation,
  useAdminRestaurarConfiguracaoMutation,
  useAdminInvalidarCacheConfiguracaoMutation,
  useAdminObterEnderecoEmpresaQuery,
  useAdminListarIdentificadoresQuery,
  useAdminCriarIdentificadorMutation,
  useAdminCriarIdentificadoresEmFaixaMutation,
  useAdminAtualizarIdentificadorMutation,
  useAdminRemoverIdentificadorMutation,
  useAdminGerarCodigoPareamentoMutation,
  useAdminStatusPareamentoQuery,
  useAdminDesvincularAparelhoMutation,
  useResgatarPareamentoMutation,
  useAdminVerificarSubdominioQuery,
  useAdminResumoIdentificadoresQuery,
  useAdminListarConfigPontosAcessoQuery,
  useAdminSalvarConfigPontoAcessoMutation,
  useAdminDuplicarConfigPontoAcessoMutation,
  useLazyAdminExportarIdentificadoresQuery,
  useAdminListarComandasSischefQuery,
  useLazyAdminListarComandasSischefQuery,
  useAdminListarCuponsQuery,
  useAdminCriarCupomMutation,
  useAdminAtualizarCupomMutation,
  useAdminRemoverCupomMutation,
  useAdminListarRedesQuery,
  useAdminListarLojasCandidatasRedeQuery,
  useAdminCriarRedeMutation,
  useAdminAtualizarRedeMutation,
  useAdminListarLojasDaRedeQuery,
  useAdminSalvarLojasDaRedeMutation,
  useAdminListarHorariosQuery,
  useAdminCriarHorarioMutation,
  useAdminAtualizarHorarioMutation,
  useAdminRemoverHorarioMutation,
  useAdminListarPausasQuery,
  useAdminPausarCanalMutation,
  useAdminListarFormasPagamentoQuery,
  useAdminListarFormasPagamentoSischefQuery,
  useAdminListarIntegracoesPagamentoQuery,
  useAdminWebhookAsaasQuery,
  useAdminDiagnosticoPixQuery,
  useAdminCriarFormaPagamentoMutation,
  useAdminAtualizarFormaPagamentoMutation,
  useAdminRemoverFormaPagamentoMutation,
  useAdminListarTaxasBairroQuery,
  useAdminCriarTaxaBairroMutation,
  useAdminAtualizarTaxaBairroMutation,
  useAdminRemoverTaxaBairroMutation,
  useAdminListarTaxasImportaveisSischefQuery,
  useAdminImportarTaxasSischefMutation,
  useAdminDiagnosticoKmQuery,
  useLazyAdminDiagnosticoKmQuery,
  useAdminListarTaxasKmQuery,
  useAdminCriarTaxaKmMutation,
  useAdminAtualizarTaxaKmMutation,
  useAdminRemoverTaxaKmMutation,
  useAdminListarAvaliacoesQuery,
  useAdminRemoverAvaliacaoMutation,
  useAdminListarPedidosQuery,
  useAdminReenviarPedidoMutation,
  useAdminForcarIntegracaoMutation,
  useAdminListarMunicipiosAtendidosQuery,
  useAdminAdicionarMunicipioAtendidoMutation,
  useAdminRemoverMunicipioAtendidoMutation,
  useAdminBuscarMunicipiosQuery,
  useLazyAdminBuscarMunicipiosQuery,
  useAdminGerarPresignedUploadMutation,
  useAdminListarRecorteCatalogoQuery,
  useAdminSalvarRecorteCatalogoMutation,
} = api;
